/** A whole `<script>…</script>` element. The HTML parser ends a script at the
 *  first `</script`, so a script body can never contain one. */
const SCRIPT_ELEMENT = /<script\b[^>]*>[\s\S]*?<\/script\s*>/gi;

/** The operator's widget HTML with its scripts taken out. Server-rendered
 *  scripts would run once per Book button slot; `runWidgetScripts` runs each
 *  one once per page instead. */
export function withoutScripts(html: string): string {
  return html.replace(SCRIPT_ELEMENT, "");
}

const started = new Set<string>();

/**
 * Appends the widget HTML's scripts to `<body>`, each at most once per page
 * load: a vendor loader shared by the sidebar, every date row and the sticky
 * bar must not start twice. A script is known by its `src`, or by its text
 * when it has none.
 *
 * A script added with `innerHTML` never runs, so each one is rebuilt with
 * `createElement`. It copies the page's nonce, which the dynamic tree's CSP
 * needs for an inline script; the cached tree's CSP allows inline scripts
 * without one (`buildIsrCsp` in `src/proxy.ts`). A `src` still has to be
 * on `APP_STORE_SCRIPT_HOSTS`.
 */
export function runWidgetScripts(html: string, doc: Document = document): void {
  const parsed = new DOMParser().parseFromString(html, "text/html");
  const nonce = pageNonce(doc);
  for (const source of parsed.querySelectorAll("script")) {
    const key = source.src || source.text;
    if (started.has(key)) continue;
    started.add(key);
    const script = doc.createElement("script");
    for (const attr of source.attributes) {
      script.setAttribute(attr.name, attr.value);
    }
    script.text = source.text;
    if (nonce) script.setAttribute("nonce", nonce);
    doc.body.append(script);
  }
}

/** Browsers hide a parsed nonce attribute (it reads back empty) and keep the
 *  value on the `nonce` property. */
function pageNonce(doc: Document): string | undefined {
  const own = doc.querySelector<HTMLScriptElement>("script[nonce]");
  return own ? own.nonce || own.getAttribute("nonce") || undefined : undefined;
}
